// src/pages/purchase/MySiteConfirmations.js
// Direct Site Purchase — Site Engineer's scoped entry point.
// Shows ONLY: POs on projects they're assigned to (site_engineer_ids
// array-contains their uid) AND purchase_mode === "direct_site_purchase"
// AND still awaiting their confirmation. Stock Purchase POs — and
// Direct Site Purchase POs already confirmed/finalized — never appear
// here (least-privilege, matches every other role-scoping pattern in
// this app).

import { useEffect, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { useNavigate } from "react-router-dom";

const userData = () => JSON.parse(localStorage.getItem("user") || "{}");

export default function MySiteConfirmations() {
  const navigate = useNavigate();
  const me       = userData();

  const [loading, setLoading] = useState(true);
  const [pos,     setPos]     = useState([]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    try {
      const myUid = me.uid || me.id;

      // Step 1: which projects am I assigned to?
      const projSnap = await getDocs(query(
        collection(db, "projects"),
        where("company_id", "==", me.company_id || "")
      ));
      const myProjectIds = projSnap.docs
        .filter(d => (d.data().site_engineer_ids || []).includes(myUid))
        .map(d => d.id);

      if (myProjectIds.length === 0) { setPos([]); setLoading(false); return; }

      // Step 2: Direct Site Purchase POs on those projects, still
      // pending my confirmation. Firestore 'in' caps at 30 — fine for
      // a per-engineer project count in this business.
      const poSnap = await getDocs(query(
        collection(db, "purchase_orders"),
        where("company_id", "==", me.company_id || ""),
        where("purchase_mode", "==", "direct_site_purchase"),
        where("project_id", "in", myProjectIds.slice(0, 30))
      ));

      const pending = poSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(po =>
          po.site_confirmation_status === "pending_site_confirmation" &&
          po.po_status === "ordered" &&
          // Empty assigned_site_engineer_ids → project-wide fallback,
          // everyone on the project sees it (existing behaviour).
          // Non-empty → ONLY the pinned engineer(s) see it.
          (!(po.assigned_site_engineer_ids?.length > 0) ||
            po.assigned_site_engineer_ids.includes(myUid))
        )
        .sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0));

      setPos(pending);
    } catch (err) {
      console.error("[MySiteConfirmations] load error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-gray-900">Site Deliveries — Confirm Receipt</h1>
        <p className="text-sm text-gray-500 mt-1">
          Direct Site Purchases on your assigned projects, awaiting your confirmation.
        </p>
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl p-10 text-center shadow-sm border border-slate-100 text-gray-500">
          Loading...
        </div>
      ) : pos.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center shadow-sm border border-slate-100">
          <p className="text-gray-500">No deliveries waiting for confirmation.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pos.map(po => (
            <div
              key={po.id}
              onClick={() => navigate(`/purchase-orders/confirm-site/${po.id}`)}
              className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 cursor-pointer hover:shadow-md transition"
              style={{ borderLeft: "4px solid #7c3aed" }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-base font-bold text-slate-800">{po.po_number || "PO"}</h2>
                    <span className="text-[10px] bg-purple-100 text-purple-700 font-bold px-2 py-0.5 rounded-full">
                      Direct Site Purchase
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 mt-0.5">
                    {po.vendor_name || "No Vendor"} · {po.project_name} — {po.site_name}
                  </p>
                </div>
                <span className="text-xs font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-700 shrink-0">
                  Awaiting You
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}